/*
 * Figma `121:1344` — LIVE-02 · Show Day command center.
 *
 * `OperationsTemplate`: the run of show is the focus, because it is the thing being operated; the
 * state of the run sits above it, and the incidents and load-in board underneath.
 *
 * Five tiles, not six: the load-in count used to have one and now lives on the load-in card itself,
 * which lists the same rounds a few inches below. A strip stops being scannable somewhere around
 * five, and a tile that repeats the card under it is the first one to lose.
 *
 * **Incidents are their own record, not the risk register.** The frame's right-hand column and its
 * header action are both about incidents, and TICK-142 built them: a title, a severity, when it
 * started in the world, an owner, a status, and an append-only decision log. The risk register is
 * still not shown here and never will be — a risk is a thing that might happen, with a mitigation,
 * and drawing one under a heading that says "Active incidents" would turn a plan into a false
 * report from the floor.
 *
 * The delay tile is real too, now that a cue records how many minutes late it is. What is still
 * missing is the frame's "อยู่ใน buffer": there is no buffer anywhere to compare against, so the
 * tile says how far behind the worst cue is and stops there.
 *
 * And its attendance tile says "ผู้อยู่ในพื้นที่". Nothing scans anyone out, so what the system
 * knows is how many were admitted, which is a different number the moment the first person leaves.
 * The tile says the number it can defend.
 *
 * What the frame leaves out is the load-in board, which is real, is on this destination already,
 * and matters on the same morning.
 */
import { useState } from 'react';
import {
  Alert, AuditTimeline, Button, Card, ConfirmDialog, FeedbackState, FormDialog, KpiCard,
  OperationsTemplate, Select, StatusBadge, TextField, Textarea, Toggle,
} from '../..';
import type { AuditEvent, BadgeTone } from '../..';
import type { Destination } from '../../destinations';
import type {
  OpsCueDto, OpsCueInput, OpsCueStatus, OpsDockInput, OpsDockStatus, OpsDockSummaryDto,
  OpsIncidentDto, OpsIncidentSeverity,
} from '../../../api';
import type { RunCue, ShowDayState, ShowStatus } from './useShowDay';
import { pendingDocks, useShowDay } from './useShowDay';

const DOCK_STATUS_LABEL: Record<OpsDockStatus, string> = {
  scheduled: 'ยังไม่มาถึง', arrived: 'มาถึงแล้ว', unloading: 'กำลังลงของ', completed: 'ลงของเสร็จ',
};

const emptyDock = (): OpsDockInput => ({
  dockNumber: '', vendorName: '', equipmentType: '', scheduledTime: '',
  truckPlate: '', driverPhone: '',
});

const CUE_STATUS_LABEL: Record<OpsCueStatus, string> = {
  pending: 'ยังไม่ถึง', ready: 'พร้อม', executed: 'ผ่านไปแล้ว', delayed: 'ดีเลย์',
};
const CUE_STATUS_TONE: Record<OpsCueStatus, BadgeTone> = {
  pending: 'neutral', ready: 'accent', executed: 'success', delayed: 'danger',
};

const SHOW_STATE: Record<ShowStatus['state'], { text: string; tone: 'positive' | 'warning' | 'danger' | 'neutral' }> = {
  not_started: { text: 'ยังไม่เริ่ม', tone: 'neutral' },
  on_track: { text: 'ตามแผน', tone: 'positive' },
  delayed: { text: 'มีคิวดีเลย์', tone: 'danger' },
  finished: { text: 'จบแล้ว', tone: 'positive' },
};

const SEVERITY_LABEL: Record<OpsIncidentSeverity, string> = {
  critical: 'CRITICAL', high: 'HIGH', medium: 'MEDIUM', low: 'LOW',
};
const SEVERITY_TONE: Record<OpsIncidentSeverity, BadgeTone> = {
  critical: 'danger', high: 'warning', medium: 'accent', low: 'neutral',
};
const INCIDENT_STATUS_LABEL: Record<OpsIncidentDto['status'], string> = {
  open: 'เปิดอยู่', mitigating: 'กำลังแก้', resolved: 'ปิดแล้ว',
};

const time = (iso: string) => new Date(iso).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });

const emptyDraft = (): OpsCueInput => ({
  title: '', time: '', durationMinutes: 15, departmentId: null, ownerStaffId: null,
  fixedDeadline: false, notes: '',
});

export function ShowDayScreen({ destination, state, permitted, role }: {
  destination: Destination;
  state: ShowDayState;
  permitted: boolean;
  role?: string | null;
}) {
  const {
    run, status, incidents, incidentSummary, docks, departments, crew, stats,
    loading, loadError, error, busyId,
    setCueStatus, setCueDelay, saveCue, removeCue,
    openIncident, setIncidentStatus, setIncidentSeverity, recordDecision, reload,
    saveDock, setDockStatus, removeDock, revealDock, revealedDock, clearRevealedDock,
  } = state;

  const [editing, setEditing] = useState<{ cue: OpsCueDto | null } | null>(null);
  const [draft, setDraft] = useState<OpsCueInput>(emptyDraft());
  const [confirmDelete, setConfirmDelete] = useState<OpsCueDto | null>(null);
  const [reporting, setReporting] = useState(false);
  const [incidentDraft, setIncidentDraft] = useState<{ title: string; severity: OpsIncidentSeverity; detail: string }>(
    { title: '', severity: 'medium', detail: '' },
  );
  const [deciding, setDeciding] = useState<OpsIncidentDto | null>(null);
  const [decision, setDecision] = useState({ decision: '', rationale: '' });
  const [editingDock, setEditingDock] = useState<{ dock: OpsDockSummaryDto | null } | null>(null);
  const [dockDraft, setDockDraft] = useState<OpsDockInput>(emptyDock());
  const [deletingDock, setDeletingDock] = useState<OpsDockSummaryDto | null>(null);

  const waitingDocks = pendingDocks(docks);
  // TICK-153 — people (wristbands), not orders: one QR collects the whole order's wristbands.
  const admitted = stats?.checkin.peopleCheckedIn ?? stats?.checkin.checkedIn ?? 0;
  const paid = stats?.checkin.peoplePaid ?? stats?.checkin.totalPaid ?? 0;

  const openCreate = () => { setDraft(emptyDraft()); setEditing({ cue: null }); };
  const openEdit = (cue: OpsCueDto) => {
    setDraft({
      title: cue.title, time: cue.time, durationMinutes: cue.durationMinutes,
      departmentId: cue.departmentId, ownerStaffId: cue.ownerStaffId,
      status: cue.status, fixedDeadline: cue.fixedDeadline, notes: cue.notes,
    });
    setEditing({ cue });
  };

  return (
    <OperationsTemplate
      gate={{ destination, permitted, role, loading, error: loadError || null, onRetry: reload }}
      kpis={
        <>
          <KpiCard
            label="สถานะรันงาน"
            value={SHOW_STATE[status.state].text}
            tone={SHOW_STATE[status.state].tone}
            meta={status.total === 0
              ? 'ยังไม่มีคิวในระบบ'
              : `${status.doneCount}/${status.total} คิวผ่านไปแล้ว${status.delayedCount > 0 ? ` · ดีเลย์ ${status.delayedCount} คิว` : ''}`}
          />
          <KpiCard
            label="คิวถัดไป"
            value={status.next ? status.next.cue.time : '—'}
            meta={status.next
              ? `${status.next.cue.title}${status.next.owner ? ` · ${status.next.owner}` : ''}`
              : status.total === 0 ? 'ยังไม่มีคิว' : 'ทุกคิวผ่านไปแล้ว'}
          />
          <KpiCard
            label="เช็กอินเข้างานแล้ว"
            value={admitted.toLocaleString('th-TH')}
            /* Not "ผู้อยู่ในพื้นที่": nothing scans anyone out, so this is how many came in. */
            meta={paid > 0
              ? `${((admitted / paid) * 100).toFixed(0)}% ของ ${paid.toLocaleString('th-TH')} คนที่ชำระแล้ว · ไม่ได้นับคนที่ออกไปแล้ว`
              : 'ยังไม่มีออร์เดอร์ที่ชำระเงินแล้ว'}
          />
          <KpiCard
            label="Incident ที่เปิดอยู่"
            value={incidentSummary.open.toLocaleString('th-TH')}
            tone={incidentSummary.critical > 0 ? 'danger' : incidentSummary.open > 0 ? 'warning' : 'neutral'}
            meta={incidentSummary.critical > 0
              ? `${incidentSummary.critical} เรื่องระดับ critical · บันทึกการตัดสินใจแล้ว ${incidentSummary.decisions} ครั้ง`
              : `บันทึกการตัดสินใจแล้ว ${incidentSummary.decisions} ครั้ง`}
          />
          <KpiCard
            label="ดีเลย์มากสุด"
            value={status.worstDelayMinutes > 0 ? `${status.worstDelayMinutes} นาที` : 'ไม่มี'}
            tone={status.worstDelayMinutes > 0 ? 'warning' : 'neutral'}
            /* The worst single cue, not the sum: delays on a running order overlap, and adding them
             * would claim a lateness nobody experienced. There is no buffer to compare against. */
            meta={status.worstDelayMinutes > 0
              ? 'คิวที่ช้าที่สุดบนบอร์ด — ไม่ได้รวมกันทุกคิว'
              : 'ยังไม่มีคิวไหนบันทึกว่าช้า'}
          />

        </>
      }
      result={error ? <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert> : undefined}
      focus={
        <Card
          title="Run of show"
          sub="ลำดับมาจากสถานะที่ทีมงานกดไว้ ไม่ใช่จากนาฬิกา — คิวเก็บเวลาในหนึ่งวัน แต่ไม่ได้เก็บวันที่"
          action={<Button variant="primary" onClick={openCreate}>เพิ่มคิว</Button>}
        >
          {run.length === 0 ? (
            <FeedbackState state="empty" title="ยังไม่มีคิวบนเวที">
              เพิ่มคิวแรกเพื่อให้ทีมงานรันงานจากหน้านี้ได้
            </FeedbackState>
          ) : (
            <ul className="jt-checks">
              {run.map((item) => (
                <CueRow
                  key={item.cue.id}
                  item={item}
                  busy={busyId === item.cue.id}
                  onStatus={(next) => void setCueStatus(item.cue, next)}
                  onDelay={(minutes) => void setCueDelay(item.cue, minutes)}
                  onEdit={() => openEdit(item.cue)}
                  onDelete={() => setConfirmDelete(item.cue)}
                />
              ))}
            </ul>
          )}
        </Card>
      }
      context={
        <>
          <Card
            title="Active incidents"
            sub="สิ่งที่กำลังเกิดขึ้นจริงหน้างาน — ไม่ใช่ทะเบียนความเสี่ยง ซึ่งเป็นสิ่งที่อาจเกิด"
            action={<Button variant="primary" onClick={() => setReporting(true)}>บันทึก Incident</Button>}
          >
            {incidents.length === 0 ? (
              <FeedbackState state="empty" title="ยังไม่มีเหตุการณ์ที่บันทึกไว้">
                บันทึกไว้ตอนเกิดจริง แล้วค่อยลงว่าตัดสินใจอะไร — พรุ่งนี้เช้าจะได้อ่านย้อนได้
              </FeedbackState>
            ) : (
              <ul className="jt-checks">
                {incidents.map((incident) => (
                  <IncidentRow
                    key={incident.id}
                    incident={incident}
                    busy={busyId === incident.id}
                    onStatus={(next) => void setIncidentStatus(incident, next)}
                    onSeverity={(next) => void setIncidentSeverity(incident, next)}
                    onDecide={() => { setDecision({ decision: '', rationale: '' }); setDeciding(incident); }}
                  />
                ))}
              </ul>
            )}
          </Card>

          <Card
            title="รอบลงของ"
            sub={docks.length === 0
              ? 'นัดหมายรถและอุปกรณ์ของวันนี้'
              : `ยังไม่มาถึง ${waitingDocks.length} จากทั้งหมด ${docks.length} รอบ`}
          >
            {docks.length === 0 ? (
              <p className="jt-field-hint">ยังไม่มีรอบลงของ — เพิ่มรอบแรกเพื่อเริ่มจัดคิวรถ</p>
            ) : (
              <ul className="jt-checks">
                {docks.map((dock) => (
                  <li key={dock.id} className="jt-check jt-dock-row">
                    <div className="jt-check-copy">
                      <p className="jt-check-label">{dock.dockNumber} · {dock.vendorName}</p>
                      <p className="jt-check-detail">
                        {dock.equipmentType || 'ไม่ระบุอุปกรณ์'} · นัด {dock.scheduledTime || '—'}
                        {dock.truckPlate ? ` · ${dock.truckPlate}` : ''}
                      </p>
                      {/*
                        * The plate is on the board because a marshal reads it off a windscreen;
                        * the driver's number is a person's and stays masked until asked for.
                        */}
                      <p className="jt-check-detail jt-mono">
                        {revealedDock?.id === dock.id ? revealedDock.driverPhone : dock.driverPhoneMasked}
                      </p>
                      {permitted && (
                        <div className="jt-inline-actions">
                          <Select
                            label={`สถานะของ ${dock.dockNumber}`}
                            value={dock.status}
                            disabled={busyId === dock.id}
                            options={(Object.keys(DOCK_STATUS_LABEL) as OpsDockStatus[])
                              .map((v) => ({ value: v, label: DOCK_STATUS_LABEL[v] }))}
                            onChange={(e) => void setDockStatus(dock, e.target.value as OpsDockStatus)}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyId === dock.id}
                            onClick={() => {
                              setDockDraft({
                                dockNumber: dock.dockNumber, vendorName: dock.vendorName,
                                equipmentType: dock.equipmentType, scheduledTime: dock.scheduledTime,
                                truckPlate: dock.truckPlate, driverPhone: '',
                              });
                              setEditingDock({ dock });
                            }}
                          >
                            แก้ไข
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => (revealedDock?.id === dock.id
                              ? clearRevealedDock()
                              : void revealDock(dock.id))}
                          >
                            {revealedDock?.id === dock.id ? 'ซ่อนเบอร์' : 'ดูเบอร์คนขับ'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyId === dock.id}
                            onClick={() => setDeletingDock(dock)}
                          >
                            ลบรอบ
                          </Button>
                        </div>
                      )}
                    </div>
                    <span className="jt-check-trail">
                      <StatusBadge tone={dock.status === 'scheduled' ? 'warning' : 'success'}>
                        {dock.status === 'scheduled'
                          ? DOCK_STATUS_LABEL.scheduled
                          : dock.arrivedTime || DOCK_STATUS_LABEL[dock.status]}
                      </StatusBadge>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {permitted && (
              <div className="jt-inline-actions">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => { setDockDraft(emptyDock()); setEditingDock({ dock: null }); }}
                >
                  ＋ เพิ่มรอบลงของ
                </Button>
              </div>
            )}
          </Card>

          <Alert tone="warning" title="สิ่งที่หน้านี้ยังทำไม่ได้">
            <ul className="jt-consequences">
              <li>ไม่มี buffer ของรันงานให้เทียบ — บอกได้ว่าช้ากี่นาที แต่บอกไม่ได้ว่ายังอยู่ในแผนหรือเลยไปแล้ว</li>
              <li>ทะเบียนความเสี่ยงไม่ใช่ incident — เป็นสิ่งที่อาจเกิดพร้อมแผนรับมือ ไม่ใช่สิ่งที่กำลังเกิดอยู่ จึงไม่เอามาแสดงตรงนี้</li>
              <li>บันทึกการตัดสินใจแก้หรือลบไม่ได้ ตั้งใจให้เป็นแบบนั้น — ถ้าตัดสินใจใหม่ ให้บันทึกเพิ่มอีกบรรทัด</li>
            </ul>
          </Alert>

          <FormDialog
            open={reporting}
            onClose={() => setReporting(false)}
            onSubmit={() => {
              void openIncident({
                title: incidentDraft.title,
                severity: incidentDraft.severity,
                detail: incidentDraft.detail,
              }).then((ok) => {
                if (!ok) return;
                setReporting(false);
                setIncidentDraft({ title: '', severity: 'medium', detail: '' });
              });
            }}
            title="บันทึก Incident"
            description="บันทึกตอนนี้ แล้วค่อยลงว่าตัดสินใจอะไร — เวลาที่เริ่มเกิดจะถูกบันทึกเป็นตอนนี้"
            submitLabel="บันทึก"
            busy={busyId === 'new-incident'}
            error={error || null}
          >
            <TextField
              label="เกิดอะไรขึ้น"
              value={incidentDraft.title}
              onChange={(e) => setIncidentDraft({ ...incidentDraft, title: e.target.value })}
              requiredMark
              placeholder="เช่น คนแน่นหน้าประตู B"
            />
            <Select
              label="ระดับความรุนแรง"
              value={incidentDraft.severity}
              onChange={(e) => setIncidentDraft({ ...incidentDraft, severity: e.target.value as OpsIncidentSeverity })}
              options={(Object.keys(SEVERITY_LABEL) as OpsIncidentSeverity[]).map((k) => ({ value: k, label: SEVERITY_LABEL[k] }))}
            />
            <Textarea
              label="รายละเอียด"
              value={incidentDraft.detail}
              onChange={(e) => setIncidentDraft({ ...incidentDraft, detail: e.target.value })}
              hint="สิ่งที่คนมารับช่วงต่อกลางดึกต้องรู้"
            />
          </FormDialog>

          <FormDialog
            open={deciding !== null}
            onClose={() => setDeciding(null)}
            onSubmit={() => {
              const target = deciding;
              if (!target) return;
              void recordDecision(target, decision.decision, decision.rationale).then((ok) => {
                if (ok) setDeciding(null);
              });
            }}
            title={`บันทึกการตัดสินใจ · ${deciding?.title ?? ''}`}
            description="บันทึกแล้วแก้หรือลบไม่ได้ — ถ้าเปลี่ยนใจทีหลัง ให้บันทึกเพิ่มอีกบรรทัด"
            submitLabel="บันทึกการตัดสินใจ"
            busy={busyId === deciding?.id}
            error={error || null}
          >
            <TextField
              label="ตัดสินใจอะไร"
              value={decision.decision}
              onChange={(e) => setDecision({ ...decision, decision: e.target.value })}
              requiredMark
              placeholder="เช่น หยุดรับคนเข้าประตู B ชั่วคราว"
            />
            <Textarea
              label="เพราะอะไร"
              value={decision.rationale}
              onChange={(e) => setDecision({ ...decision, rationale: e.target.value })}
              hint="เหตุผลตอนนั้น — พรุ่งนี้เช้าคนอ่านจะไม่ได้อยู่ตรงนั้นด้วย"
            />
          </FormDialog>

          <FormDialog
            open={editing !== null}
            onClose={() => setEditing(null)}
            onSubmit={() => {
              void saveCue(draft, editing?.cue ?? null).then((ok) => { if (ok) setEditing(null); });
            }}
            title={editing?.cue ? `แก้ไขคิว ${editing.cue.title}` : 'เพิ่มคิวบนเวที'}
            submitLabel={editing?.cue ? 'บันทึก' : 'เพิ่มคิว'}
            busy={busyId !== null}
            error={error || null}
          >
            <TextField
              label="ชื่อคิว"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              requiredMark
              placeholder="เช่น Opening VTR"
            />
            <TextField
              label="เวลา"
              type="time"
              value={draft.time}
              onChange={(e) => setDraft({ ...draft, time: e.target.value })}
              requiredMark
              hint="เวลาในหนึ่งวัน — ระบบไม่ได้เก็บวันที่ของคิว"
            />
            <TextField
              label="ความยาว (นาที)"
              inputMode="numeric"
              value={String(draft.durationMinutes)}
              onChange={(e) => setDraft({ ...draft, durationMinutes: Number(e.target.value.replace(/[^0-9]/g, '')) || 0 })}
            />
            <Select
              label="ฝ่ายที่รับผิดชอบ"
              value={draft.departmentId ?? ''}
              onChange={(e) => setDraft({ ...draft, departmentId: e.target.value || null })}
              placeholder="ยังไม่ระบุฝ่าย"
              options={departments.map((d) => ({ value: d.id, label: d.name }))}
            />
            <Select
              label="เจ้าของคิว"
              value={draft.ownerStaffId ?? ''}
              onChange={(e) => setDraft({ ...draft, ownerStaffId: e.target.value || null })}
              placeholder="ยังไม่ระบุเจ้าของ"
              options={crew.map((c) => ({ value: c.id, label: c.displayName }))}
            />
            <Toggle
              label="เลื่อนไม่ได้"
              checked={draft.fixedDeadline ?? false}
              onChange={(e) => setDraft({ ...draft, fixedDeadline: e.target.checked })}
              consequence="ใช้กับคิวที่มีข้อผูกพันข้างนอก เช่น เคอร์ฟิวสถานที่ หรือคิวถ่ายทอดสด"
            />
            <Textarea
              label="โน้ต"
              value={draft.notes ?? ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              hint="สิ่งที่คนอ่านคิวนี้หน้างานต้องรู้"
            />
          </FormDialog>

          <ConfirmDialog
            open={confirmDelete !== null}
            onClose={() => setConfirmDelete(null)}
            onConfirm={() => {
              const target = confirmDelete;
              setConfirmDelete(null);
              if (target) void removeCue(target);
            }}
            title={`ลบคิว ${confirmDelete?.title ?? ''}`}
            confirmLabel="ลบคิว"
            busy={busyId !== null}
            consequences={[
              'คิวนี้จะหายไปจาก run of show ทันที และทีมงานที่เปิดหน้านี้อยู่จะไม่เห็นอีก',
              'โน้ตและเจ้าของที่ผูกไว้กับคิวนี้จะหายไปด้วย',
              'ลบแล้วกู้คืนไม่ได้ — ถ้าแค่ไม่ได้ใช้ในรอบนี้ ให้เปลี่ยนสถานะแทน',
            ]}
          />

          <FormDialog
            open={editingDock !== null}
            onClose={() => setEditingDock(null)}
            onSubmit={() => {
              void saveDock(dockDraft, editingDock?.dock ?? null)
                .then((ok) => { if (ok) setEditingDock(null); });
            }}
            title={editingDock?.dock ? `แก้ไขรอบ ${editingDock.dock.dockNumber}` : 'เพิ่มรอบลงของ'}
            description="นัดหมายรถและอุปกรณ์ของวันงาน — เบอร์คนขับถูกปิดบังในบอร์ด และดูได้ทีละคัน"
            submitLabel={editingDock?.dock ? 'บันทึกรอบนี้' : 'เพิ่มรอบลงของ'}
            busy={busyId === (editingDock?.dock?.id ?? 'new-dock')}
            error={error || null}
          >
            <TextField
              label="หมายเลขรอบ"
              value={dockDraft.dockNumber}
              onChange={(e) => setDockDraft({ ...dockDraft, dockNumber: e.target.value })}
              hint="เช่น D1 · ใช้เรียกกันหน้างาน"
            />
            <TextField
              label="ผู้ขาย / ผู้ส่ง"
              value={dockDraft.vendorName}
              onChange={(e) => setDockDraft({ ...dockDraft, vendorName: e.target.value })}
            />
            <TextField
              label="อุปกรณ์"
              value={dockDraft.equipmentType}
              onChange={(e) => setDockDraft({ ...dockDraft, equipmentType: e.target.value })}
              hint="เช่น เครื่องเสียง · ไฟ · โครงเวที"
            />
            <TextField
              label="เวลานัด"
              value={dockDraft.scheduledTime}
              onChange={(e) => setDockDraft({ ...dockDraft, scheduledTime: e.target.value })}
              hint="เช่น 09:00"
            />
            <TextField
              label="ทะเบียนรถ"
              value={dockDraft.truckPlate}
              onChange={(e) => setDockDraft({ ...dockDraft, truckPlate: e.target.value })}
              hint="สิ่งที่คนเฝ้าประตูอ่านจากกระจกหน้ารถเพื่อจับคู่กับรอบนี้"
            />
            <TextField
              label="เบอร์คนขับ"
              value={dockDraft.driverPhone}
              onChange={(e) => setDockDraft({ ...dockDraft, driverPhone: e.target.value })}
              hint={editingDock?.dock
                ? 'เว้นว่างไว้ = ไม่เปลี่ยนเบอร์เดิม · กรอกเมื่อต้องการแทนที่เท่านั้น'
                : 'ใช้ตอนต้องโทรตาม — ถูกปิดบังในบอร์ด'}
            />
          </FormDialog>

          <ConfirmDialog
            open={deletingDock !== null}
            onClose={() => setDeletingDock(null)}
            onConfirm={() => {
              const target = deletingDock;
              setDeletingDock(null);
              if (target) void removeDock(target);
            }}
            title={`ลบรอบ ${deletingDock?.dockNumber ?? ''}`}
            confirmLabel="ลบรอบนี้"
            busy={busyId !== null}
            consequences={[
              'รอบนี้จะหายไปจากบอร์ดลงของทันที',
              'ทะเบียนรถและเบอร์คนขับที่ผูกไว้จะหายไปด้วย',
              'ถ้ารถแค่ยังไม่มา ให้ใช้สถานะแทนการลบ',
            ]}
          />
        </>
      }
    />
  );
}

/**
 * One incident, with its decision log underneath.
 *
 * The log is rendered even when it is empty, because an incident with no decisions on it is itself
 * the thing a supervisor is looking for: something is wrong and nobody has recorded doing anything
 * about it yet.
 */
function IncidentRow({ incident, busy, onStatus, onSeverity, onDecide }: {
  incident: OpsIncidentDto;
  busy: boolean;
  onStatus: (next: OpsIncidentDto['status']) => void;
  onSeverity: (next: OpsIncidentSeverity) => void;
  onDecide: () => void;
}) {
  const events: AuditEvent[] = incident.decisions.map((d) => ({
    id: d.id,
    actor: d.actor,
    action: d.decision,
    at: new Date(d.decidedAt).toLocaleString('th-TH'),
    note: d.rationale || undefined,
  }));

  return (
    <li className="jt-check jt-incident" data-severity={incident.severity} data-resolved={incident.status === 'resolved' || undefined}>
      <div className="jt-check-copy">
        <p className="jt-check-label">
          <StatusBadge tone={SEVERITY_TONE[incident.severity]}>{SEVERITY_LABEL[incident.severity]}</StatusBadge>
          {' '}{incident.title}
        </p>
        <p className="jt-check-detail">
          เริ่ม {time(incident.startedAt)}
          {incident.ownerName ? ` · ${incident.ownerName}` : incident.departmentName ? ` · ${incident.departmentName}` : ''}
          {incident.resolvedAt ? ` · ปิดเมื่อ ${time(incident.resolvedAt)}` : ''}
        </p>
        {incident.detail && <p className="jt-check-detail">{incident.detail}</p>}

        <div className="jt-incident-log">
          <AuditTimeline
            events={events}
            label={`บันทึกการตัดสินใจของ ${incident.title}`}
            empty="ยังไม่มีการตัดสินใจที่บันทึกไว้"
          />
        </div>

        <div className="jt-cue-actions">
          <Select
            label={`ระดับความรุนแรงของ ${incident.title}`}
            className="jt-cue-status"
            value={incident.severity}
            disabled={busy}
            onChange={(e) => onSeverity(e.target.value as OpsIncidentSeverity)}
            options={(Object.keys(SEVERITY_LABEL) as OpsIncidentSeverity[]).map((k) => ({ value: k, label: SEVERITY_LABEL[k] }))}
          />
          <Select
            label={`สถานะของ ${incident.title}`}
            className="jt-cue-status"
            value={incident.status}
            disabled={busy}
            onChange={(e) => onStatus(e.target.value as OpsIncidentDto['status'])}
            options={(Object.keys(INCIDENT_STATUS_LABEL) as OpsIncidentDto['status'][])
              .map((k) => ({ value: k, label: INCIDENT_STATUS_LABEL[k] }))}
          />
          <Button size="sm" variant="secondary" onClick={onDecide}>บันทึกการตัดสินใจ</Button>
        </div>
      </div>
    </li>
  );
}

function CueRow({ item, busy, onStatus, onDelay, onEdit, onDelete }: {
  item: RunCue;
  busy: boolean;
  onStatus: (next: OpsCueStatus) => void;
  onDelay: (minutes: number) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { cue, position, owner } = item;
  return (
    <li className="jt-check jt-cue" data-position={position}>
      <span className="jt-cue-time jt-mono">{cue.time || '—'}</span>
      <div className="jt-check-copy">
        <p className="jt-check-label">
          {cue.title}
          {position === 'next' && <span className="jt-cue-next"> · คิวถัดไป</span>}
          {cue.fixedDeadline && <span className="jt-check-blocking"> · เลื่อนไม่ได้</span>}
        </p>
        <p className="jt-check-detail">
          {owner ?? 'ยังไม่มีเจ้าของ'} · {cue.durationMinutes} นาที
          {cue.delayMinutes > 0 && <span className="jt-cue-late"> · ช้า {cue.delayMinutes} นาที</span>}
        </p>
        {cue.notes && <p className="jt-check-detail">{cue.notes}</p>}
      </div>
      <span className="jt-check-trail jt-cue-actions">
        <StatusBadge tone={CUE_STATUS_TONE[cue.status]}>{CUE_STATUS_LABEL[cue.status]}</StatusBadge>
        <Select
          label={`สถานะของ ${cue.title}`}
          className="jt-cue-status"
          value={cue.status}
          disabled={busy}
          onChange={(e) => onStatus(e.target.value as OpsCueStatus)}
          options={(Object.keys(CUE_STATUS_LABEL) as OpsCueStatus[]).map((k) => ({ value: k, label: CUE_STATUS_LABEL[k] }))}
        />
        <TextField
          label={`ดีเลย์ของ ${cue.title} (นาที)`}
          className="jt-cue-delay"
          inputMode="numeric"
          defaultValue={String(cue.delayMinutes)}
          disabled={busy}
          /* Committed on blur, not on every keystroke: each write is a versioned round trip, and
           * one per digit would fight itself on a board somebody is typing into during a show. */
          onBlur={(e) => {
            const next = Number(e.target.value.replace(/[^0-9]/g, '')) || 0;
            if (next !== cue.delayMinutes) onDelay(next);
          }}
        />
        <Button size="sm" variant="ghost" onClick={onEdit}>แก้ไข</Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>ลบ</Button>
      </span>
    </li>
  );
}

export function ShowDayDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useShowDay(token, eventSlug);
  return <ShowDayScreen destination={destination} state={state} permitted={permitted} role={role} />;
}
